import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { TableColumnsType } from 'antd';

import { useUserContext } from '../../../../../context/PermissionsContext';
import { useBusinessFunctionData } from '../../../../../hooks/business-functions/queries';

import { Footer, TitleBar } from '../../../../../components';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';

import { tableSortingMethods } from '../../../../../utils';

import {
  FETCH_BUSINESS_FUNCTIONS_WORKAROUNDS_SUMMARY,
  UPDATE_BUSINESS_FUNCTIONS_WORKAROUNDS,
} from './queries';

import { getWorkaroundSummaryFormFields } from './utils';
import { useSyncRuntimeHours } from './hooks';
import { renderComplianceBadges } from '../../../utils/complianceBadgeRenderer';
import {
  BusinessFunctionWorkaround,
  GetBusinessFunctionWorkaroundsSummaryResponse,
  WORKAROUND_TYPES,
  WORKAROUND_DISPLAY_LABELS,
  WorkaroundDefinedType,
} from '../../../../../shared/types/business-functions-workarounds';
import { CollapsibleText } from '../../../../../components/generic/TextTruncation';

const WorkaroundsBusinessFunctionsSummary = () => {
  // NOTE: since we need watch method from React Hook Form, I create a new instance and pass it down to props
  const formInstance = useForm();
  const { watch, setValue } = formInstance;

  const { canEdit } = useUserContext();
  const { businessFunctionId } = useParams();

  const { businessFunctionName, businessFnError } =
    useBusinessFunctionData(businessFunctionId);

  const {
    data: workaroundsBusinessFunctionsSummaryData,
    loading: workaroundsBusinessFunctionsSummaryLoading,
    error: workaroundsBusinessFunctionsSummaryError,
  } = useQuery<GetBusinessFunctionWorkaroundsSummaryResponse>(
    FETCH_BUSINESS_FUNCTIONS_WORKAROUNDS_SUMMARY,
    {
      variables: {
        data: {
          uuids: [businessFunctionId],
        },
      },
    },
  );

  const [
    updateBusinessFunctionsWorkaround,
    { loading: updateBusinessFunctionsWorkaroundLoading },
  ] = useMutation(UPDATE_BUSINESS_FUNCTIONS_WORKAROUNDS, {
    refetchQueries: ['GetBusinessFunctionWorkarounds'],
  });

  const workaroundNeeded = watch('isWorkaroundNeeded');
  const workaroundDefined: WorkaroundDefinedType = watch('workaroundDefined');
  const runtimeHours = watch('runtimeHours');

  const runtimeHoursEnabled =
    workaroundDefined === WORKAROUND_TYPES.DEFINED_TIME;

  const tableData =
    workaroundsBusinessFunctionsSummaryData?.getBusinessFunctionWorkarounds ??
    [];

  const loadingStates =
    workaroundsBusinessFunctionsSummaryLoading ||
    updateBusinessFunctionsWorkaroundLoading;

  const columns: TableColumnsType<BusinessFunctionWorkaround> = [
    {
      title: 'IT Application',
      dataIndex: 'itApplicationName',
      key: 'itApplicationName',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.itApplicationName, b.itApplicationName),
      render: (itApplicationName: string) => (
        <span className="custom-table--column ml-5">{itApplicationName}</span>
      ),
    },
    {
      title: (
        <span>
          Workaround
          <br />
          Needed?
        </span>
      ),
      dataIndex: 'isWorkaroundNeeded',
      key: 'isWorkaroundNeeded',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.boolean(a.isWorkaroundNeeded, b.isWorkaroundNeeded),
      render: (isWorkaroundNeeded: boolean) => (
        <span className="custom-table--column">
          {isWorkaroundNeeded ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      title: (
        <span>
          Workaround
          <br />
          Timeline Compliance
        </span>
      ),
      dataIndex: 'workaroundTimelineCompliance',
      key: 'workaroundTimelineCompliance',
      align: 'center',
      width: 250,
      render: (complianceData, workaroundRecord) => {
        return renderComplianceBadges(complianceData, {
          isWorkaroundNeeded: workaroundRecord.isWorkaroundNeeded,
        });
      },
    },
    {
      title: (
        <span>
          Workaround
          <br />
          Defined
        </span>
      ),
      dataIndex: 'workaroundDefined',
      key: 'workaroundDefined',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.string(a.workaroundDefined, b.workaroundDefined),
      render: (
        workaroundDefined: BusinessFunctionWorkaround['workaroundDefined'],
      ) => {
        const workaroundValue = WORKAROUND_DISPLAY_LABELS[workaroundDefined];

        return workaroundValue ===
          WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND] ? (
          <span className="text-[#BABABA]">{workaroundValue}</span>
        ) : (
          <span className="custom-table--column">{workaroundValue}</span>
        );
      },
    },
    {
      title: (
        <span>
          Runtime with
          <br />
          Workaround
        </span>
      ),
      dataIndex: 'runtimeHours',
      key: 'runtimeHours',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.runtimeHours, b.runtimeHours),
      render: (runtimeHours: number) => {
        let runtime: string = `${runtimeHours} hours`;

        if (!runtimeHours) {
          runtime = 'N/A';
        } else if (runtimeHours === -1) {
          runtime = 'Infinite';
        }
        return runtime === 'N/A' ? (
          <span className="text-[#BABABA]">{runtime}</span>
        ) : (
          <span>{runtime}</span>
        );
      },
    },
    {
      title: (
        <span>
          Maximum Allowable
          <br />
          Data Loss
        </span>
      ),
      dataIndex: 'maxDataLossHours',
      key: 'maxDataLossHours',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.maxDataLossHours, b.maxDataLossHours),
      render: (maxDataLossHours: number) => {
        const dataLossTime: string = `${maxDataLossHours ?? 0} hours`;

        return <span className="custom-table--column">{dataLossTime}</span>;
      },
    },
    {
      title: 'Workaround Description',
      dataIndex: 'workaroundDescription',
      key: 'workaroundDescription',
      width: 200,
      align: 'left',
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const formFields = getWorkaroundSummaryFormFields(
    workaroundNeeded,
    runtimeHoursEnabled,
  );

  const modifyVariables = useCallback(
    ({ values }: ModifyVariablesArgs) => {
      const {
        itApplicationId,
        workaroundDescription,
        workaroundDefined,
        maxDataLossHours,
        isWorkaroundNeeded,
        runtimeHours,
      } = values;

      const isInfinite =
        runtimeHours === WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.INFINITE];
      const isNoWorkaround =
        runtimeHours ===
        WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND];
      const shouldSendRuntimeHours = isInfinite || isNoWorkaround;

      let normalizedRuntimeHours;

      if (!shouldSendRuntimeHours) {
        normalizedRuntimeHours = runtimeHours ? Number(runtimeHours) : null;
      }

      return {
        businessFunctionUuid: businessFunctionId,
        itApplicationUuid: itApplicationId,
        isWorkaroundNeeded: Boolean(isWorkaroundNeeded),
        workaroundDefined: workaroundDefined,
        runtimeHours: normalizedRuntimeHours,
        maxDataLossHours: maxDataLossHours ? Number(maxDataLossHours) : null,
        workaroundDescription,
      };
    },
    [businessFunctionId],
  );

  useSyncRuntimeHours({
    workaroundNeeded,
    workaroundDefined,
    runtimeHours,
    setValue,
  });

  return (
    <>
      <TitleUpdater title="Business Function Workarounds" />
      <QueryErrorBoundary error={businessFnError}>
        <TitleBar
          title="Enterprise Business Function Workarounds"
          subtitle={`${businessFunctionName} Applications`}
          brief={`Capture and describe how the ${businessFunctionName} Function can work around a disruption of the following critical applications.`}
        />
        <QueryErrorBoundary error={[workaroundsBusinessFunctionsSummaryError]}>
          <TableV3
            title={(currentRecord: BusinessFunctionWorkaround) =>
              `${currentRecord?.itApplicationName} / Edit Workaround`
            }
            data={tableData}
            columns={columns}
            loading={loadingStates}
            updateGQL={updateBusinessFunctionsWorkaround}
            editable
            editableOptions={{
              update: canEdit,
              delete: false,
            }}
            formFields={formFields}
            modifyVariables={modifyVariables}
            hideTableHeading
            formInstance={formInstance} // this is for dynamic formFields behaviour
            tableSize="small"
          />
        </QueryErrorBoundary>
        <Footer
          backPath={`/cyber/enterprise-level/application-workarounds/business-functions`}
          goNextText="Overview"
          nextPath="/cyber/enterprise-level/application-workarounds"
        />
      </QueryErrorBoundary>
    </>
  );
};

export default WorkaroundsBusinessFunctionsSummary;
